import { api } from 'api';
import { Fade } from 'helpers/animation';
import { checkMaintenance } from 'helpers/maintenance';
import useFormInput from 'hooks/form/useFormInput';
import useDebounceCallback from 'hooks/useDebounceCallback';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useLang from 'hooks/useLang';
import { isEmpty } from 'lodash';
import Form from 'react-bootstrap/Form';
import styled from 'styled-components';
import {
  REGISTER_ADDRESS_STATE_NAME,
  REGISTER_ADDRESS_SUBURB_NAME,
} from '../../../../../constants';
import {
  DEBOUNCE_SEC,
  DESKTOP_MIN_WIDTH,
  RESET_BACKDROP_STORE,
} from '../../../../../constants/common';
import useStore from '../../../../../hooks/useStore';
import { SET_SHOW_BACKDROP } from '../../../../../store/action';
import ArrowDownIcon from '../../../../common/Icons/ArrowDownIcon';

const FormInputAddressPostcodeValidation = ({
  ref,
  field,
  information,
  setInformation,
  validation,
  setValidation,
  postcodeSuggestion,
  setPostcodeSuggestion,
  checkFormInputValidation,
}) => {
  const { t } = useLang();
  const { state, dispatch } = useStore();
  const { backdrop } = state;
  const { isDesktop } = useDetectDevice();

  const {
    inputRef,
    resetFormInput,
    setInformationFormInput,
    setValidationFormInput,
    getValueTrim,
    FormLabel,
    FormControlFeedback,
  } = useFormInput(field);

  const fetchLookupPostcodeSuggestion = async (value) => {
    try {
      const { data } = await api.lookupPostcodes(
        value,
        'd6fe8ff05352dfff3c3a3367d7140364'
      );

      if (data?.payload) {
        setPostcodeSuggestion(data.payload);
      }
    } catch (error) {
      checkMaintenance(error);
      console.error(error?.message);
    }
  };

  const checkLookupPostcodeSuggestion = async (value) => {
    await fetchLookupPostcodeSuggestion(value);

    dispatch({
      type: SET_SHOW_BACKDROP,
      payload: {
        isShow: true,
        current: field.name,
      },
    });
  };

  const { debounce: handleLookupPostcodeSuggestion } = useDebounceCallback(
    (value) => {
      checkLookupPostcodeSuggestion(value);
    },
    DEBOUNCE_SEC
  );

  const handleOnChange = (e) => {
    const { value } = e.target;
    let valueTrim = getValueTrim(value);

    if (!valueTrim) {
      let newInformation = {
        ...information,
        [REGISTER_ADDRESS_SUBURB_NAME]: '',
        [REGISTER_ADDRESS_STATE_NAME]: '',
      };

      resetFormInput({
        information: newInformation,
        setInformation,
        validation,
        setValidation,
      });

      setPostcodeSuggestion(null);

      dispatch({
        type: SET_SHOW_BACKDROP,
        payload: RESET_BACKDROP_STORE,
      });

      return;
    }

    handleLookupPostcodeSuggestion(valueTrim);

    setInformationFormInput({
      value: valueTrim,
      information,
      setInformation,
    });

    setValidationFormInput({
      validation,
      setValidation,
      checkFormInputValidation: () =>
        checkFormInputValidation(valueTrim, field, validation),
    });

    dispatch({
      type: SET_SHOW_BACKDROP,
      payload: {
        isShow: !backdrop?.isShow,
        current: field.name,
        onClick: () => {
          document.getElementsByName(field.name)[0].value = '';
          setValidationFormInput({
            validation,
            setValidation,
            checkFormInputValidation: () =>
              checkFormInputValidation(valueTrim, field, validation),
          });
        },
      },
    });
  };

  const handleOnBlur = (e) => {
    handleOnChange(e);
  };

  const handleOnClick = () => {
    const newInformation = {
      ...information,
      [field.name]: '',
    };

    setInformation(newInformation);

    setPostcodeSuggestion(null);

    dispatch({
      type: SET_SHOW_BACKDROP,
      current: RESET_BACKDROP_STORE.current,
      payload: {
        isShow: false,
        onClick: () => {
          document.getElementsByName(field.name)[0].value = '';

          const newValidation = checkFormInputValidation('', field, validation);
          setValidation(newValidation);
        },
      },
    });
  };

  const renderArrowDownIcon = () => {
    if (isEmpty(postcodeSuggestion)) return null;
    return (
      <ArrowDownIcon
        color="var(--ds-c-grey-dark)"
        size={{ width: 12, height: 12 }}
        styles={{
          position: 'absolute',
          top: isDesktop ? '242px' : '232px',
          right: '16px',
          transform: 'rotate(180deg)',
          cursor: 'pointer',
        }}
        onClick={handleOnClick}
      />
    );
  };

  return (
    <FormGroupStyled>
      <FormLabel />
      <FormControl
        ref={ref}
        type={field.type}
        name={field.name}
        defaultValue={information[field.name]}
        placeholder={t(field.placeholder)}
        isInvalid={validation[field.name]}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        onClick={handleOnClick}
      />
      <FormControlFeedback value={t(validation[field.name])} />
      {renderArrowDownIcon()}
    </FormGroupStyled>
  );
};

const FormGroupStyled = styled(Form.Group)`
  position: relative;
  margin-bottom: 32px;
  ${Fade};

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    margin-bottom: 34px;
  }
`;

const FormControl = styled(Form.Control)`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;

  color: var(--ds-c-grey-dark);
  height: 44px;
  border: 1px solid var(--ds-c-grey-light);
  border-radius: 12px;
  padding-inline: 10px;
  background: var(--ds-c-white);

  &:focus {
    border: 1px solid var(--ds-c-grey-dark) !important;
    outline: none !important;
    box-shadow: none !important;
  }

  &:invalid,
  &:invalid:focus,
  &.is-invalid {
    border: 1px solid var(--ds-c-red) !important;
    outline: none !important;
    box-shadow: none !important;
    background-image: none !important;
  }

  &[readonly] {
    outline: none !important;
    box-shadow: none !important;
    background-color: transparent !important;
  }

  &[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }

  &::placeholder {
    font-family: var(--ff-primary);
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;

    color: var(--ds-c-blue-disabled) !important;

    @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
      font-size: 16px;
      line-height: 20px;
    }
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

export default FormInputAddressPostcodeValidation;
